import React from "react";
import { colors } from "../theme";

const F = "'Montserrat',sans-serif";

/**
 * AiSignals — reusable on-demand AI insight panel.
 *
 * Renders a compact, grounded list of AI signals as coloured-dot bullets, with a
 * Generate/Refresh button and loading / error / empty states. Stateless: the
 * parent owns fetching and passes the results in.
 *
 * Used by the Management dashboard ("Where to focus") and Market Insights
 * ("Market read") so the panel looks and behaves identically in one place.
 *
 * @param {string}  title        uppercase panel label (e.g. "Where to focus")
 * @param {Array}   signals      [{type, text}] once generated; null before first run
 * @param {boolean} loading      request in flight
 * @param {string}  error        error message to show (optional)
 * @param {func}    onGenerate   click handler for the Generate/Refresh button
 * @param {object}  colorByType  map of signal.type → dot colour
 * @param {string}  emptyText    hint shown before generating / when no signals
 */
export default function AiSignals({ title, signals, loading, error, onGenerate, colorByType = {}, emptyText }) {
  const hint = emptyText || "Click Generate for AI insights from these numbers.";
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: colors.text.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>{title}</div>
        <button onClick={onGenerate} disabled={loading} style={{
          padding: "4px 10px", borderRadius: 6, border: `1px solid ${colors.brand.primary}`,
          background: colors.brand.primaryTint, color: colors.brand.primary, fontSize: 10, fontWeight: 700,
          cursor: loading ? "default" : "pointer", fontFamily: F, whiteSpace: "nowrap", opacity: loading ? 0.6 : 1,
        }}>{loading ? "Analysing…" : signals ? "↻ Refresh" : "✨ Generate"}</button>
      </div>

      {error ? (
        <div style={{ fontSize: 10.5, color: colors.status.bad, lineHeight: 1.45 }}>{error}</div>
      ) : signals ? (
        signals.length === 0
          ? <div style={{ fontSize: 10.5, color: colors.text.faint }}>No signals for the current data.</div>
          : signals.map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 9, fontSize: 11.5, color: colors.text.body, lineHeight: 1.45 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: colorByType[s.type] || colors.brand.primary, marginTop: 4, flexShrink: 0 }} />
                <span>{s.text}</span>
              </div>
            ))
      ) : (
        <div style={{ fontSize: 10.5, color: colors.text.faint, lineHeight: 1.45 }}>{hint}</div>
      )}
    </div>
  );
}
